import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import i18next from 'i18next';

import {ReportConfigsService, RolesService} from '../../services';
import ReportForm from './report_form';
import { withFixedFeatureFlags } from './feature_flags';
import {PageTitle} from '../PageTitle';
import { Message } from '../Message';

// Translated when rendered: this module is imported before i18n is initialised, so translating here yields blank names
const REPORT_TYPES = [
    {configName: "ALL_DEVICES_REPORT", labelKey: 'pages.all-assets-report.header'},
    {configName: "ALL_DEVICES_30_DAY_REPORT", labelKey: 'pages.scheduled-reports.all-assets-30-day'},
    {configName: "CORRECTIVE_ACTIONS_REPORT", labelKey: 'pages.corrective-actions.header'}
];

const getReportDisplayName = (reportType) => {
    const type = REPORT_TYPES.find(reportTypeEntry => reportTypeEntry.configName === reportType);
    return type ? i18next.t(type.labelKey) : 'Report';
};

const ScheduledReports = ({ me }) => {
    const [loading, setLoading] = useState(true);
    const [localSuccessMessage, setLocalSuccessMessage] = useState(null);
    const [localErrorMessage, setLocalErrorMessage] = useState(null);
    const [scheduledReports, setScheduledReports] = useState([]);
    const [roleOptions, setRoleOptions] = useState([]);
    const [timezones, setTimezones] = useState([]);
    const [days, setDays] = useState([]);

    useEffect(() => {
        setLoading(true);

        let requests = [];
        let scheduledReports = [];

        REPORT_TYPES.forEach(type => {
            requests.push(
                ReportConfigsService.getReportConfig(me.enterprise.id, type.configName).then(response => {
                    let config = response.config ? response.config : {reportType: type.configName};
                    scheduledReports.push(config);
                }).catch(err => {
                    console.error(err);
                    setLocalErrorMessage(i18next.t('pages.scheduled-reports.no-settings-for-type', {type: getReportDisplayName(type.configName)}));
                })
            );
        });

        requests.push(
            RolesService.listAllForRoleType('group_role').then(response => {
                setRoleOptions(response.roles.map((role) => ({ value: role.id, label: role.name })));
            }).catch(error => {
                console.error(error);
                setLocalErrorMessage(error.message);
            })
          )

        moment.locale(i18next.language);
        setTimezones(moment.tz.names().map(timezone => ({value: timezone, label: timezone})));
        setDays([
            { value: "monday", label: moment.weekdays(1) },
            { value: "tuesday", label: moment.weekdays(2) },
            { value: "wednesday", label: moment.weekdays(3) },
            { value: "thursday", label: moment.weekdays(4) },
            { value: "friday", label: moment.weekdays(5) },
            { value: "saturday", label: moment.weekdays(6) },
            { value: "sunday", label: moment.weekdays(0) },
        ]);

        Promise.all(requests).then(() => {
            setScheduledReports(scheduledReports.sort((a, b) => a.reportType.localeCompare(b.reportType)));
            setLoading(false);
        });
    }, [me]);

    const handleSubmit = useCallback(({active, reportType, role, timezone, dayOfWeek, timeOfDay, advanced}) => {
        setLoading(true);

        const configToUpdate = scheduledReports.find(report => report.reportType === reportType);
        const displayName = getReportDisplayName(reportType);

        if (active) {
            const submittedConfig = {
                reportType: reportType, 
                enterprise: me.enterprise,
                role: {id: role.value, name: role.label},
                timezone: timezone.value,
                dayOfWeek: dayOfWeek.value,
                timeOfDay: timeOfDay,
                featureFlags: withFixedFeatureFlags(reportType, advanced)
            };

            ReportConfigsService.addReportConfig(submittedConfig).then(response => {
                setScheduledReports(scheduledReports.map((reportConfig) => (reportConfig.reportType === response.config.reportType ? response.config : reportConfig)));
                setLocalErrorMessage(null);
                setLocalSuccessMessage(i18next.t('pages.scheduled-reports.configuration-saved-on', {report: displayName}));
                setLoading(false);
            }).catch(err => {
                console.error(err);

                setScheduledReports(scheduledReports.map((reportConfig) => (reportConfig.reportType === submittedConfig.reportType ? submittedConfig : reportConfig)));
                setLocalErrorMessage(i18next.t('pages.scheduled-reports.unable-to-save', {report: displayName}));
                setLocalSuccessMessage(null);
                setLoading(false);
            });
        } else if (configToUpdate && configToUpdate.id) {
            ReportConfigsService.deleteReportConfig(configToUpdate.id).then(_ => {
                setScheduledReports(scheduledReports.map((reportConfig) => (reportConfig.reportType === reportType ? { reportType } : reportConfig)));
                setLocalSuccessMessage(i18next.t('pages.scheduled-reports.configuration-saved-off', {report: displayName}));
                setLoading(false);
            }).catch(err => {
                console.error(err);

                setLocalErrorMessage(i18next.t("pages.scheduled-reports.unable-to-disable", { report: displayName }));
                setLoading(false);
            })
        }
    }, [me, scheduledReports]);

    if (loading) {
        return (<div className="loader">{i18next.t('loading')}</div>);
    } else {
        return (
            <div>
                <PageTitle title={i18next.t("pages.scheduled-reports.heading")} />

                {localErrorMessage && <Message errorMessage={localErrorMessage} messageDismissCallback={setLocalErrorMessage(null)} />}
                {localSuccessMessage && <Message messages={{ state: { successMessage: localSuccessMessage } }} messageDismissCallback={setLocalSuccessMessage(null)} />}

                {scheduledReports.map((report, index) => (
                    <div key={index} className="op-content-box">
                        <h4>{getReportDisplayName(report.reportType)}</h4>
                        <ReportForm form={report.reportType} report={report} enterprise={me.enterprise} roleOptions={roleOptions} timezones={timezones} days={days} onSubmit={handleSubmit} />
                    </div>
                ))}
            </div>
        );
    }
};

export default connect((state) => ({me: state.user.me}))(ScheduledReports);